import { Chip, Typography } from '@execonline-inc/execonline-ui';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { fromEmpty, just, Maybe, nothing } from 'maybeasy';
import { useState } from 'react';
import { Tooltip } from 'react-tooltip';
import { T } from '../../../../../../Translations';
import { PluralizedText } from '../../../../../PluralizedText';
import {
  isAssignable,
  isWorthLabelling,
  statusClassName,
  statusLabel,
} from '../LicenseAvailability';
import { GrantingLicense, ProgramFamily } from '../LicenseAlertsStore/Types';

interface Props {
  courses: ReadonlyArray<ProgramFamily>;
  // A group that can enroll in nothing has nothing else to read here, so the
  // list opens with the group rather than hiding behind a second click.
  initiallyOpen: boolean;
}

const VISIBLE_BEFORE_TRUNCATION = 3;

// Several licenses can unlock the same course. Name every one the organization
// could actually assign today, in the server's order (highest tier first), and
// fall back to the first only when it holds none.
const remedies = (course: ProgramFamily): ReadonlyArray<GrantingLicense> => {
  const assignable = course.grantingLicenses.filter((license) => isAssignable(license.status));

  return assignable.length > 0 ? assignable : course.grantingLicenses.slice(0, 1);
};

// Everything except the ones named beside the course. These are what the count
// in "N other licenses cover this course" is actually counting.
const alternateLicenses = (course: ProgramFamily): ReadonlyArray<GrantingLicense> => {
  const named = remedies(course);

  return course.grantingLicenses.filter((license) => !named.includes(license));
};

const sameLicenses = (
  a: ReadonlyArray<GrantingLicense>,
  b: ReadonlyArray<GrantingLicense>,
): boolean =>
  a.length === b.length &&
  a.every((license, index) => license.name === b[index].name && license.status === b[index].status);

// When the same licenses unlock every course in the list, say so once at the
// top rather than repeating long names under each title. Nothing when the
// courses disagree, or when any of them has no license to name at all.
const sharedRemedies = (
  courses: ReadonlyArray<ProgramFamily>,
): Maybe<ReadonlyArray<GrantingLicense>> =>
  fromEmpty([...courses]).andThen((all) =>
    fromEmpty([...remedies(all[0])]).andThen((first) =>
      all.every((course) => sameLicenses(remedies(course), first))
        ? just<ReadonlyArray<GrantingLicense>>(first)
        : nothing<ReadonlyArray<GrantingLicense>>(),
    ),
  );

// Where the chips sit: once above every course, or under one course.
type ChipsPlacement = 'shared' | 'course';

interface LicenseChipsProps {
  licenses: ReadonlyArray<GrantingLicense>;
  placement: ChipsPlacement;
}

// The lead-in is what makes these read as licenses rather than as courses or
// tags. License names run long, so they are never set inside a sentence.
function LicenseChips({ licenses, placement }: LicenseChipsProps) {
  return (
    <div
      className="flex flex-col gap-1"
      data-intercom-target={`share-license-alert-unlocking-licenses-${placement}`}
    >
      <Typography variant="caption" className="text-muted-foreground">
        <PluralizedText
          count={licenses.length}
          singularKind="Unlocked with this license:"
          pluralKind="Unlocked with any one of these licenses:"
        />
      </Typography>
      <div className="flex flex-wrap items-center gap-2">
        {licenses.map((license) => (
          <span key={license.name} className="inline-flex items-center gap-2">
            <Chip className="w-fit bg-muted text-muted-foreground" size="sm" radius="full">
              {license.name}
            </Chip>
            {isWorthLabelling(license.status) && (
              <Typography variant="caption" className={statusClassName(license.status)}>
                <T kind={statusLabel(license.status)} />
              </Typography>
            )}
          </span>
        ))}
      </div>
    </div>
  );
}

interface AlternateLicensesProps {
  course: ProgramFamily;
}

function AlternateLicenses({ course }: AlternateLicensesProps) {
  const alternates = alternateLicenses(course);

  if (alternates.length === 0) {
    return <></>;
  }

  return (
    <>
      <Typography
        variant="caption"
        as="span"
        className="w-fit cursor-help text-muted-foreground underline decoration-dotted underline-offset-2"
        data-tooltip-id={`share-license-alternates-${course.id}`}
        data-intercom-target="share-license-alert-alternate-licenses"
      >
        <T kind="{{others}} other licenses cover this course." others={alternates.length} />
      </Typography>
      {/* The modal sits at z-50 and its body scrolls, so the tooltip has to
          clear both. */}
      <Tooltip
        id={`share-license-alternates-${course.id}`}
        place="top"
        positionStrategy="fixed"
        className="!border !border-border !opacity-100 shadow-md"
        style={{
          borderRadius: '6px',
          zIndex: 60,
          backgroundColor: 'var(--card)',
          color: 'var(--card-foreground)',
        }}
      >
        {/* Licence names are labels, not sentences. Wrapped over two lines one
            name reads as two licences. */}
        <div className="flex flex-col gap-0.5 whitespace-nowrap py-1 text-xs">
          {alternates.map((alternate, index) => (
            <div key={`${alternate.name}-${index}`} className="flex items-baseline gap-2">
              <span>{alternate.name}</span>
              {isWorthLabelling(alternate.status) && (
                <span className="text-muted-foreground">
                  <T kind={statusLabel(alternate.status)} />
                </span>
              )}
            </div>
          ))}
        </div>
      </Tooltip>
    </>
  );
}

function CoursesNeedingALicense({ courses, initiallyOpen }: Props) {
  const [open, setOpen] = useState(initiallyOpen);
  const [expanded, setExpanded] = useState(false);
  const ChevronIcon = open ? ChevronUpIcon : ChevronDownIcon;

  const shared = sharedRemedies(courses);
  const shown = expanded ? courses : courses.slice(0, VISIBLE_BEFORE_TRUNCATION);
  const remaining = courses.length - shown.length;

  return (
    <div
      className="border-b border-border"
      data-intercom-target="share-license-alert-courses-section"
    >
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-4 py-3 text-left hover:bg-secondary"
        data-intercom-target="share-license-alert-courses-needing-license"
      >
        <Typography variant="bodyMedium" fontWeight="medium" className="flex-1">
          {courses.length}{' '}
          {shared.isNothing() ? (
            <T kind="courses need other licenses" />
          ) : (
            <PluralizedText
              count={courses.length}
              singularKind="course needs another license"
              pluralKind="courses need another license"
            />
          )}
        </Typography>
        <ChevronIcon className="h-4 w-4 flex-none text-muted-foreground" aria-hidden="true" />
      </button>

      {open && (
        <div className="flex flex-col gap-3 px-4 pb-4">
          {shared
            .map((licenses) => <LicenseChips key="shared" licenses={licenses} placement="shared" />)
            .getOrElseValue(<></>)}

          <ul
            className="flex list-disc flex-col gap-2 pl-5"
            data-intercom-target="share-license-alert-course-list"
          >
            {shown.map((course) => (
              <li key={course.id} data-intercom-target="share-license-alert-course">
                <Typography variant="bodyMedium">{course.name}</Typography>
                {fromEmpty([...remedies(course)])
                  .map((licenses) => (
                    <div key="licenses" className="mt-1 flex flex-col gap-1">
                      {shared.isNothing() && (
                        <LicenseChips licenses={licenses} placement="course" />
                      )}
                      <AlternateLicenses course={course} />
                    </div>
                  ))
                  .getOrElse(() => (
                    <Typography variant="caption" className="block text-muted-foreground">
                      <T kind="No license you can assign covers this course." />
                    </Typography>
                  ))}
              </li>
            ))}
          </ul>

          {remaining > 0 && (
            <button
              type="button"
              onClick={() => setExpanded(true)}
              className="w-fit text-primary hover:underline"
              data-intercom-target="share-license-alert-show-more-courses"
            >
              <Typography variant="bodyMedium">
                <T kind="Show {{remaining}} more" remaining={remaining} />
              </Typography>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default CoursesNeedingALicense;
